/**
 * ====================================================================
 * ITEM SELECTION OVERLAY COMPONENT
 * ====================================================================
 *
 * @description Overlay component for selecting inventory items with global user support
 * @author Samuel Kow Paintsil
 * @modifiedBy Arutprakasam A
 * @modifiedAt 01-07-2025
 * @modfiedctblNumber CTBL-335
 *
 * ====================================================================
 * GLOBAL USER INTEGRATION
 * ====================================================================
 *
 * OVERVIEW:
 * This component provides item selection functionality that integrates
 * with the global user permission system for consistent access control.
 *
 * KEY FEATURES:
 * - Item catalog browsing and selection
 * - Responsive overlay positioning
 * - Integration with sidebar collapse state
 * - Support for global user permission context
 *
 * PERMISSION CONSIDERATIONS:
 * - Respects global user permissions for item access
 * - Maintains consistent behavior across different user types
 * - Supports permission-based item availability
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Whether the overlay is visible
 * @param {HTMLElement} props.anchorEl - Element to anchor the overlay to
 * @param {Array} props.items - List of items to display
 * @param {Array} props.selectedItemIds - IDs of currently selected items
 * @param {boolean} props.isLoading - Whether items are being loaded
 * @param {function} props.onClose - Handler for closing the overlay
 * @param {function} props.onSelectItem - Handler for item selection
 * @param {function} props.onAddItems - Handler for adding selected items
 * @param {function} props.onAddNewItem - Handler for creating new item
 *
 * ====================================================================
 */
import React from "react";
import PropTypes from "prop-types";
import {
  Popper,
  Paper,
  Button,
  CircularProgress,
  Checkbox,
  ClickAwayListener,
} from "@mui/material";
import CustomTable from "../table/table";
import "./item-selection-overlay.scss";
import { toUSD } from "src/util/currency-formatter";
import { useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";

const ItemSelectionOverlay = ({
  open,
  anchorEl,
  items,
  selectedItemIds,
  isLoading,
  onClose,
  onSelectItem,
  onAddItems,
  onAddNewItem,
}) => {
  const isSidebarCollapsed = useSelector(
    (state) => state.common.isSidebarCollapsed
  );
  const renderCheckbox = (item) => {
    const itemId = `${item.id}${item.cost}`;
    return (
      <Checkbox
        checked={selectedItemIds.includes(itemId)}
        onClick={(e) => {
          e.stopPropagation();
          onSelectItem(itemId);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        size="small"
        sx={{
          padding: "0px",
          "& .MuiSvgIcon-root": {
            fontSize: "18px",
          },
        }}
      />
    );
  };

  const renderCatalog = (item) => {
    return item.category && item.category.name ? item.category.name : "";
  };

  const renderPartNumber = (item) => {
    return item.partNo || "";
  };

  const renderCost = (item) => {
    return toUSD(item.cost);
  };

  // const renderItemDescription = (item) => {
  //     return <p className="item-selection-overlay-cell-name">{item.item}
  //     <span className='ms-2'>
  //         {item.unit}
  //     </span>
  //     </p>
  // }

  // Table column configuration
  const columns = [
    {
      field: "select",
      title: "",
      render: renderCheckbox,
      class: "cell-checkbox",
    },
    {
      field: "category",
      title: "Catalog",
      render: renderCatalog,
      class: "cell-catalog",
    },
    {
      field: "manufacturer",
      title: "Category",
      class: "cell-manufacturer",
    },
    {
      field: "partNo",
      title: "Part No",
      render: renderPartNumber,
      class: "cell-part",
    },
    {
      field: "item",
      title: "Item Description",
      // render: renderItemDescription,
      class: "cell-name",
    },
    {
      field: "unit",
      title: "UoM",
      class: "cell-uom",
    },
    {
      field: "cost",
      title: "Cost($)",
      render: renderCost,
      class: "cell-cost text-right",
    },
  ];

  const tableItemClickHandler = (item) => {
    if (item && item.id) {
      const itemId = `${item.id}${item.cost}`;
      onSelectItem(itemId);
    }
  };

  const handleClickAway = (event) => {
    // Don't close if clicking on the anchor element (input field that opened the overlay)
    if (anchorEl && anchorEl.contains(event.target)) {
      return;
    }

    onClose();
  };

  // Check if pagination should be showing
  const showPagination = items.length >= 25;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [isSidebarCollapsed ? -3 : 1, 5],
          },
        },
        {
          name: "preventOverflow",
          options: {
            boundary: "viewport", // you ensure that the overlay or popper element will not overflow outside the visible area of the browser window
            altAxis: true,
            padding: 16,
          },
        },
      ]}
      popperOptions={{
        strategy: "fixed",
        modifiers: [
          {
            name: "flip",
            enabled: true,
            options: {
              // This approach helps ensure that the overlay remains visible and accessible, adapting dynamically to the available space in the user's viewport.
              fallbackPlacements: ["top-start", "bottom-start"],
            },
          },
        ],
      }}
      className="item-selection-overlay"
      data-cy="item-selection-overlay"
      zIndex={200000}
    >
      <div className={isSidebarCollapsed ? "sidebar-collapsed" : "container"}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper elevation={8} className="paper">
            {isLoading ? (
              <div className="loading">
                <CircularProgress size={30} thickness={4} />
                <p>Loading inventory items...</p>
              </div>
            ) : (
              <>
                {items && items.length > 0 ? (
                  <div className="table-container">
                    <CustomTable
                      columns={columns}
                      data={items}
                      toolbar={false}
                      exportButton={false}
                      enableSearch={false}
                      rowClickHandler={tableItemClickHandler}
                      stickyHeader={true}
                      fullHeight={false}
                      noMaxHeight={false}
                    />

                    <div
                      className={`action-row ${!showPagination ? "no-pagination" : ""
                        }`}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onAddItems}
                        disabled={
                          !selectedItemIds || selectedItemIds.length === 0
                        }
                        size="small"
                        className="action-button"
                      >
                        Add ({selectedItemIds?.length || 0})
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="empty text-center p-4 rounded border">
                    <div className="mb-3">
                      <InfoIcon
                        className="text-primary"
                        style={{ fontSize: "2rem" }}
                      />
                    </div>
                    <p className="mb-1 fw-bold">No matching items found.</p>
                    <p className="text-muted mb-4">
                      If the item you're looking for doesn't exist, please add
                      it below before saving.
                    </p>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={onAddNewItem}
                      size="small"
                      className="add-button"
                    >
                      Add New Item
                    </Button>
                  </div>
                )}
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </div>
    </Popper>
  );
};

ItemSelectionOverlay.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  items: PropTypes.array,
  selectedItemIds: PropTypes.array,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelectItem: PropTypes.func.isRequired,
  onAddItems: PropTypes.func.isRequired,
  onAddNewItem: PropTypes.func.isRequired,
};

ItemSelectionOverlay.defaultProps = {
  items: [],
  selectedItemIds: [],
  isLoading: false,
};

export default ItemSelectionOverlay;
